import { TutorialDefinition } from './tutorialDefinitions';

/**
 * Crafting and equipment tutorials - Phase 5 Implementation
 * Covers recipe discovery, crafting mechanics, and equipment management
 */

export const CRAFTING_TUTORIAL: TutorialDefinition = {
  key: 'crafting-basics',
  title: 'Crafting & Recipe System',
  description: 'Learn to craft powerful equipment and items from gathered materials',
  category: 'crafting',
  priority: 'high',
  estimatedMinutes: 4,
  icon: '⚒️',
  prerequisites: ['gathering-basics'], // Should understand gathering first
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to Crafting!',
      content: 'Crafting transforms your gathered materials into powerful equipment, weapons, and consumables. Master the forge to create legendary gear for your heroes!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'recipe-discovery',
      title: 'Discovering Recipes',
      content: 'Recipes are discovered through various means: winning battles, reaching character levels, finding them in the world, or automatically when finding related items. Your discovered recipes appear in the crafting menu.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'recipe-list',
      title: 'Recipe Selection',
      content: 'Browse your discovered recipes in the list. Each recipe shows the item it creates, required materials, crafting difficulty, and success rate. Click a recipe to view full details.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'material-requirements',
      title: 'Material Requirements',
      content: 'Each recipe requires specific materials and quantities. Materials are color-coded by rarity: gray (Common), green (Uncommon), blue (Rare), purple (Epic), gold (Legendary). You must have all required materials to craft.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'success-rate',
      title: 'Success Rate & Quality',
      content: 'Crafting has a base success rate shown on each recipe. Higher level characters have better success rates. Successfully crafted items have a quality percentage (50%-200%) that affects their stats!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'crafting-process',
      title: 'Crafting an Item',
      content: 'Select a recipe, ensure you have materials, and click "Craft Item". The system checks materials, calculates success, and creates the item. Failures consume materials, so craft wisely!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'item-types',
      title: 'Item Types',
      content: 'You can craft various item types: Weapons (swords, bows, staves), Armor (chest, legs, helmets), Accessories (rings, necklaces, belts), and Consumables (potions, arrows). Each serves different purposes!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'quality-system',
      title: 'Item Quality Bonuses',
      content: 'Item quality ranges from 50% (failed craft) to 200% (exceptional craft). A 150% quality sword deals 50% more damage than base stats! Quality is randomly determined on successful crafts.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'recipe-levels',
      title: 'Recipe Levels & Requirements',
      content: 'Recipes have level and class requirements. You can only craft items your character meets the requirements for. Higher level recipes create more powerful gear but require rarer materials.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'crafting-tips',
      title: 'Crafting Tips',
      content: 'Stockpile common materials for practice. Save rare materials for important recipes. Higher level characters have better success rates. Craft multiple items to get high quality rolls. Equipment is essential for tough battles!',
      position: 'center',
      type: 'info'
    }
  ]
};

export const EQUIPMENT_TUTORIAL: TutorialDefinition = {
  key: 'equipment-basics',
  title: 'Equipment Management',
  description: 'Equip and manage your character\'s gear for maximum effectiveness',
  category: 'crafting',
  priority: 'medium',
  estimatedMinutes: 3,
  icon: '🛡️',
  prerequisites: ['crafting-basics'], // Should understand crafting first
  steps: [
    {
      id: 'welcome',
      title: 'Equipment System',
      content: 'Equipment provides crucial stat bonuses to your characters. Properly equipping your heroes is the difference between victory and defeat. Let\'s learn the equipment system!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'equipment-slots',
      title: '12 Equipment Slots',
      content: 'Characters have 12 equipment slots: Main Hand, Off Hand, Head, Chest, Legs, Feet, Hands, Necklace, Ring 1, Ring 2, Belt, and Back. Each slot provides different bonuses and serves unique purposes.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'two-handed-weapons',
      title: 'Two-Handed Weapons',
      content: 'Some weapons require both hands to wield! Equipping a two-handed weapon (like greatswords or staves) automatically unequips your off-hand slot. They deal more damage but prevent shields or dual-wielding.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'equipment-screen',
      title: 'Equipment Interface',
      content: 'Access equipment from the Character List by clicking "Equipment" on any character card. You\'ll see a paper doll showing all slots on the left, and your inventory of unequipped items on the right.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'equipping-items',
      title: 'Equipping Items',
      content: 'Drag items from your inventory and drop them onto equipment slots. Items can only be equipped in valid slots (weapons in weapon slots, armor in armor slots, etc.). Invalid drops are rejected.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'stat-bonuses',
      title: 'Stat Bonuses',
      content: 'Equipped items provide stat bonuses: Strength (melee damage), Vitality (HP), Agility (speed/evasion), Intelligence (magic damage), Wisdom (magic defense). Hover over items to see all their stats!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'requirements',
      title: 'Equipment Requirements',
      content: 'Items have level and class requirements. Warriors can\'t use mage staves, and level 1 characters can\'t equip level 20 gear. The equipment screen filters items to show only what your character can use.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'equipment-tips',
      title: 'Equipment Strategy',
      content: 'Balance offense and defense! Don\'t ignore accessories - rings and necklaces provide powerful bonuses. Upgrade equipment regularly as you level. Match equipment to your class and playstyle. Good gear makes all the difference!',
      position: 'center',
      type: 'info'
    }
  ]
};

export const craftingTutorials = [CRAFTING_TUTORIAL, EQUIPMENT_TUTORIAL];
